import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EmpresaCadastralResponse } from '@interfaces/corporate-registry/responses/empresa-cadastral.response';
import { CorporateRegistryCacheService } from '@services/http/corporate-registry/corporate-registry-cache.service';
import { CorporateRegistryHttpService } from '@services/http/corporate-registry/corporate-registry-http.service';
import { SharedModule } from '@src/app/shared/shared.module';
import { ErrorResponse } from '@models/commom/error-response';
import { map, Subscription } from 'rxjs';
import { RegistryCompanyCardComponent } from '@src/app/features/dashboard/commercial/pages/deal-detail/components/registry/registry-company-card/registry-company-card.component';
import { RegistryNetworkCardComponent } from '@shared/components/ownership-network/registry-network-card/registry-network-card.component';
import { RegistryPartnersCardComponent } from '@src/app/features/dashboard/commercial/pages/deal-detail/components/registry/registry-partners-card/registry-partners-card.component';
import {
  classificarErroRegistro,
  ErroRegistro,
  MENSAGEM_ERRO_REGISTRO,
} from '@shared/components/ownership-network/registry-formatters';

/**
 * Conteúdo da etapa Registro: quem é a empresa do deal na Receita Federal — as seções Cadastro,
 * Sócios e Mapa societário do Vault-Dossiê (card 2986).
 *
 * Cadastro e Sócios saem da mesma consulta (`corporate-registry/empresas/{cnpj}`); o Mapa tem as
 * dele e falha sozinho. O erro fica **dentro do card**, onde a pessoa está olhando — nunca vira
 * "Deal não encontrado".
 *
 * Exige o `CorporateRegistryCacheService` provido num ancestral (o `DealDetailComponent`): trocar
 * de etapa destrói este painel, e voltar a ele não pode reconsultar a Receita.
 */
@Component({
  selector: 'app-deal-registry-panel',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    RegistryCompanyCardComponent,
    RegistryPartnersCardComponent,
    RegistryNetworkCardComponent,
  ],
  templateUrl: './deal-registry-panel.component.html',
  styleUrl: './deal-registry-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DealRegistryPanelComponent {
  readonly cnpj = input.required<string>();
  /**
   * Mostra o mapa societário compartilhado no fim do painel (padrão de Deals). Telas que querem o
   * mapa com configuração própria (ex.: Societário) desligam aqui e usam `app-registry-network-card`.
   */
  readonly showNetwork = input<boolean>(true);

  private readonly http = inject(CorporateRegistryHttpService);
  private readonly cache = inject(CorporateRegistryCacheService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly empresa = signal<EmpresaCadastralResponse | null>(null);
  protected readonly carregando = signal(true);
  protected readonly erro = signal<ErroRegistro | null>(null);

  private assinatura: Subscription | null = null;

  constructor() {
    effect(() => {
      const cnpj = this.cnpj();
      untracked(() => this.carregar(cnpj));
    });
  }

  protected tentarDeNovo(): void {
    this.carregar(this.cnpj());
  }

  protected textoDoErro(e: ErroRegistro): string {
    return e.tipo === 'mensagem' ? e.texto : MENSAGEM_ERRO_REGISTRO[e.tipo];
  }

  private carregar(cnpj: string): void {
    this.assinatura?.unsubscribe();
    this.carregando.set(true);
    this.erro.set(null);
    // Resposta vazia vira erro DENTRO da fonte do cache: assim não fica guardada e "Tentar de novo"
    // refaz a chamada (mesmo tratamento do `semNulo` do card do mapa).
    this.assinatura = this.cache
      .obter(`${cnpj}|empresa`, () =>
        this.http.getEmpresa(cnpj).pipe(
          map(e => {
            if (!e) throw new ErrorResponse('Cadastro vazio', []);
            return e;
          })
        )
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: e => {
          this.empresa.set(e);
          this.erro.set(e ? null : { tipo: 'nao-encontrado' });
          this.carregando.set(false);
        },
        error: err => {
          this.empresa.set(null);
          this.erro.set(classificarErroRegistro(err));
          this.carregando.set(false);
        },
      });
  }
}
